<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';

// composables
import { useToast } from '../../composables/useToast';

//pinia
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';

//interfaces
import { IPagination } from '../../types/pagination.type';
import { ICompany } from '../../types/company.type';
import { ITaxes } from '../../types/taxes.type';
import { ITypeTransport } from '../../types/typeTransport.type';
import { ISecurityMeasures } from '../../types/securityMeasures.type';

//repository
import TaxesRepository from '@repositories/taxes.repository';
import TypeTransportsRepository from '@repositories/typeTransports.repository';
import CompaniesRepository from '@repositories/companies.repository';
import SecurityMeasuresRepository from '../../repositories/securityMeasures.repository';
import { useI18n } from 'vue-i18n'
import DataTable from '@components/DataTable.vue';

const isLoadingTaxes = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const { taxes: TaxesPinia } = storeToRefs(store);
const taxes = ref<ITaxes[]>([]);
const companies = ref<ICompany[]>([]);
const typeTransports = ref<ITypeTransport[]>([]);
const securityMeasures = ref<ISecurityMeasures[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedTaxes = ref<ITaxes | null>(null);
const isViewActive = ref<boolean>(true);
const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'tax',
    label: t('ajustes.taxes.tax'),
  },

  {
    key: 'restore',
    label: 'Restaurar',
  },
]);
const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: t('ajustes.taxes.id'),
    sortable: true,
  },
  {
    key: 'tax',
    label: t('ajustes.taxes.tax'),
  },
  {
    key: 'indemnificationLimitMin',
    label: t('ajustes.taxes.indemnizacion_minima'),
  },
  {
    key: 'indemnificationLimitMax',
    label: t('ajustes.taxes.indemnizacion_maxima'),
  },
  {
    key: 'primaMin',
    label: t('ajustes.taxes.prima_minima'),
  },
  {
    key: 'additionalPrima',
    label: t('ajustes.taxes.prima_adicional'),
  },
  {
    key: 'typePolicy',
    label: t('ajustes.taxes.tipo_de_poliza'),
  },
  {
    key: 'deducibles',
    label: t('ajustes.taxes.deducibles'),
  },
  {
    key: 'company',
    label: t('ajustes.taxes.compania'),
    formatter: (e: any) => e?.name || null,
  },
  {
    key: 'typePolicyTransport',
    label: t('ajustes.taxes.tipo_de_poliza_transporte'),
    formatter: (e: any) => e?.TypeName || null,
  },
  {
    key: 'riskType',
    label: t('ajustes.taxes.riesgo'),
    formatter: (e: any) => e?.description || null,
  },
  {
    key: 'securityMeasures',
    label: t('ajustes.taxes.medida_de_seguridad'),
    formatter: (e: any) => e?.name || null,
  },
  {
    key: 'calcTax',
    label: t('ajustes.taxes.calcular_tax'),
  },
  {
    key: 'taxInfo',
    label: t('ajustes.taxes.editar'),
  },
  {
    key: 'delete',
    label: t('ajustes.taxes.eliminar'),
  },
]);
const fieldsFilter: any = ref([
  {
    key: 'companyId',
    title: t('ajustes.taxes.nombre_de_la_compania'),
    type: 'select',
    label: 'name',
    placeholder: t('ajustes.taxes.buscar_por_compania'),
    formatValue: (val: any) => val?.id ?? null,
    value: null,
    options: companies,
    cols: 3,
  },
]);
const searchFormRef = ref<any>(null);
const isWeb = ref<boolean>(true);
const filteredTableActive = ref([]);
const calculateTax = ref<any>(null);

// API
const getTaxesFromAPI = async (
  page: number,
  deleted: boolean = false,
  params = {},
) => {
  isLoadingTaxes.value = true;
  try {
    const data = await TaxesRepository.getTaxes({
      page,
      deleted,
      ...params,
    });

    if (data && Array.isArray(data?.taxes)) {
      paginationData.value = data.pagination;
      updateSecurityMeasures(data.taxes);
      return data.taxes;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching SecurityMeasures:', error);
    return [];
  } finally {
    isLoadingTaxes.value = false;
  }
};

const getCompaniesFromApi = async () => {
  const data = await CompaniesRepository.getCompanies();
  companies.value = data.companies;
};

const getTypeTransportsFromAPI = async () => {
  try {
    const data = await TypeTransportsRepository.getTypeTransports();

    if (data && Array.isArray(data)) {
      typeTransports.value = data;
    }
  } catch (error) {
    console.error('Error while fetching type transports:', error);
    return [];
  }
};

const getSecurityMeasuresFromApi = async () => {
  const data = await SecurityMeasuresRepository.getAllSecurityMeasures();
  securityMeasures.value = data.securityMeasures;
};

const updateSecurityMeasures = (newSecurityMeasures: ITaxes[]) => {
  taxes.value = newSecurityMeasures;
  store.setProp({ key: 'taxes', value: newSecurityMeasures });
};

const loadSecurityMeasures = async () => {
  await getTaxesFromAPI(1, false, { search: 'WEB' });
  await getCompaniesFromApi();
  await getSecurityMeasuresFromApi();
};

onMounted(() => {
  loadSecurityMeasures();
  condicionalRenderTable(true);
  getTypeTransportsFromAPI();
});

// Create inputs from modals
const fieldsInputTaxesWeb = computed(() => {
  return [
    {
      key: 'tax',
      title: t('ajustes.taxes.tax'),
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.taxes.tax'),
      value: selectedTaxes.value?.tax,
      cols: 6,
    },
    {
      key: 'indemnificationLimitMin',
      title: t('ajustes.taxes.indemnizacion_minima'),
      type: 'number',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.taxes.indemnizacion_minima'),
      value: selectedTaxes.value?.indemnificationLimitMin,
      cols: 6,
    },
    {
      key: 'indemnificationLimitMax',
      title: t('ajustes.taxes.indemnizacion_maxima'),
      type: 'number',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.taxes.indemnizacion_maxima'),
      value: selectedTaxes.value?.indemnificationLimitMax,
      cols: 6,
    },
    {
      key: 'companyId',
      label: 'name',
      selectLabel: null,
      value: selectedTaxes.value?.company.id,
      searchable: true,
      title: t('ajustes.taxes.compania'),
      trackBy: 'id',
      type: 'select',
      placeholder: t('ajustes.taxes.nombre_de_la_compania'),
      formatValue: (val: any) => val?.id,
      cols: 6,
      options: companies,
      validations: {
        rules: {
          required: true,
        },
      },
    },
    {
      key: 'securityMeasuresId',
      label: 'name',
      selectLabel: null,
      value: selectedTaxes.value?.securityMeasures?.id,
      searchable: true,
      title: t('ajustes.taxes.medida_de_seguridad'),
      trackBy: 'id',
      type: 'select',
      placeholder: t('ajustes.taxes.medida_de_seguridad'),
      formatValue: (val: any) => val?.id,
      cols: 6,
      options: securityMeasures,
    },
    {
      key: 'riskTypeId',
      label: 'description',
      value: selectedTaxes.value?.riskType.id,
      searchable: true,
      title: t('ajustes.taxes.riesgo'),
      type: 'select',
      trackBy: 'id',
      placeholder: t('ajustes.taxes.tipo_de_riesgo'),
      formatValue: (val: any) => val?.id,
      cols: 6,
      options: [
        { id: 1, description: 'LOW' },
        { id: 2, description: 'MEDIUM' },
        { id: 3, description: 'HIGH' },
      ],
      validations: {
        rules: {
          required: true,
        },
      },
    },
    {
      key: 'typePolicyTransportId',
      label: 'typeName',
      value: selectedTaxes.value?.typePolicyTransportId,
      searchable: true,
      title: t('ajustes.taxes.tipo_de_poliza_transporte'),
      type: 'select',
      placeholder: t('ajustes.taxes.tipo_de_poliza_transporte'),
      formatValue: (val: any) => val?.id,
      cols: 6,
      options: typeTransports.value,
      validations: {
        rules: {
          required: true,
        },
      },
    },
    {
      key: 'primaMin',
      title: t('ajustes.taxes.prima_minima'),
      type: 'number',
      placeholder: t('ajustes.taxes.prima_minima'),
      value: selectedTaxes.value?.primaMin,
      cols: 6,
    },
    {
      key: 'additionalPrima',
      title: t('ajustes.taxes.prima_adicional'),
      type: 'number',
      placeholder: t('ajustes.taxes.prima_adicional'),
      value: selectedTaxes.value?.additionalPrima,
      cols: 6,
    },
    {
      key: 'deducibles',
      title: t('ajustes.taxes.deducibles'),
      type: 'text',
      placeholder: t('ajustes.taxes.deducibles'),
      value: selectedTaxes.value?.deducibles,
      cols: 6,
    },
  ];
});

const fieldsInputTaxesApi = computed(() => {
  return [
    {
      key: 'tax',
      title: t('ajustes.taxes.tax'),
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.taxes.tax'),
      value: selectedTaxes.value?.tax,
      cols: 6,
    },
    {
      key: 'indemnificationLimitMax',
      title: t('ajustes.taxes.indemnizacion_maxima'),
      type: 'number',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.taxes.indemnizacion_maxima'),
      value: selectedTaxes.value?.indemnificationLimitMax,
      cols: 6,
    },
    {
      key: 'indemnificationLimitMin',
      title: t('ajustes.taxes.indemnizacion_minima'),
      type: 'number',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.taxes.indemnizacion_minima'),
      value: selectedTaxes.value?.indemnificationLimitMin,
      cols: 6,
    },
    {
      key: 'companyId',
      label: 'name',
      selectLabel: null,
      value: selectedTaxes.value?.company?.id,
      searchable: true,
      title: t('ajustes.taxes.compania'),
      trackBy: 'id',
      type: 'select',
      placeholder: t('ajustes.taxes.nombre_de_la_compania'),
      formatValue: (val: any) => val?.id,
      cols: 6,
      options: companies,
      validations: {
        rules: {
          required: true,
        },
      },
    },
    {
      key: 'typePolicy',
      label: 'description',
      value: selectedTaxes.value?.typePolicy,
      searchable: true,
      title: t('ajustes.taxes.tipo_de_poliza'),
      type: 'select',
      trackBy: 'description',
      placeholder: t('ajustes.taxes.tipo_de_poliza'),
      formatValue: (val: any) => val?.description,
      cols: 6,
      options: [
        { id: 1, description: 'A' },
        { id: 2, description: 'B' },
        { id: 3, description: 'C' },
        { id: 4, description: 'G' },
        { id: 5, description: 'F' },
      ],
      validations: {
        rules: {
          required: true,
        },
      },
    },
    {
      key: 'typePolicyTransportId',
      label: 'typeName',
      value: selectedTaxes.value?.typePolicyTransportId,
      searchable: true,
      title: t('ajustes.taxes.tipo_de_poliza_transporte'),
      type: 'select',
      placeholder: t('ajustes.taxes.tipo_de_poliza_transporte'),
      formatValue: (val: any) => val?.id,
      cols: 6,
      options: typeTransports.value,
      validations: {
        rules: {
          required: true,
        },
      },
    },
    {
      key: 'riskTypeId',
      label: 'description',
      value: selectedTaxes.value?.riskType.id,
      searchable: true,
      title: t('ajustes.taxes.riesgo'),
      type: 'select',
      trackBy: 'id',
      placeholder: t('ajustes.taxes.tipo_de_riesgo'),
      formatValue: (val: any) => val?.id,
      cols: 6,
      options: [
        { id: 1, description: 'LOW' },
        { id: 2, description: 'MEDIUM' },
        { id: 3, description: 'HIGH' },
      ],
      validations: {
        rules: {
          required: true,
        },
      },
    },
    {
      key: 'primaMin',
      title: t('ajustes.taxes.prima_minima'),
      type: 'number',
      placeholder: t('ajustes.taxes.prima_minima'),
      value: selectedTaxes.value?.primaMin,
      cols: 6,
    },
    {
      key: 'additionalPrima',
      title: t('ajustes.taxes.prima_adicional'),
      type: 'number',
      placeholder: t('ajustes.taxes.prima_adicional'),
      value: selectedTaxes.value?.additionalPrima,
      cols: 6,
    },
  ];
});

const fieldsInputCalculateTax = computed(() => {
  return [
    {
      key: 'indemnificationLimitMax',
      title: t('ajustes.taxes.indemnizacion_maxima'),
      type: 'number',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.taxes.indemnizacion_maxima'),
      cols: 6,
    },
    {
      key: 'company',
      label: 'name',
      selectLabel: null,
      searchable: true,
      title: t('ajustes.taxes.compania'),
      trackBy: 'id',
      type: 'select',
      placeholder: t('ajustes.taxes.nombre_de_la_compania'),
      cols: 6,
      options: companies.value.filter((company: ICompany) =>
        isWeb.value
          ? company.requestType === 'WEB'
          : company.requestType === 'API',
      ),
      validations: {
        rules: {
          required: true,
        },
      },
    },
    {
      key: 'typePolicy',
      label: 'description',
      searchable: true,
      title: t('ajustes.taxes.tipo_de_poliza'),
      type: 'select',
      trackBy: 'description',
      placeholder: t('ajustes.taxes.tipo_de_poliza'),
      formatValue: (val: any) => val?.description,
      cols: 6,
      hidden: isWeb.value,
      options: [
        { id: 1, description: 'A' },
        { id: 2, description: 'B' },
        { id: 3, description: 'C' },
      ],
    },
    {
      key: 'riskType',
      label: 'description',
      searchable: true,
      title: t('ajustes.taxes.riesgo'),
      type: 'select',
      trackBy: 'id',
      placeholder: t('ajustes.taxes.tipo_de_riesgo'),
      cols: 6,
      options: [
        { id: 1, description: 'LOW' },
        { id: 2, description: 'MEDIUM' },
        { id: 3, description: 'HIGH' },
      ],
      hidden: !isWeb.value,
      validations: {
        rules: {
          required: false,
        },
      },
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getTaxesFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('ajustes.taxes.agregar_tax');
  isModalOpen.value = true;
  modalType.value = 'modalCreateTaxes';
};
const handleOpenModalCalculate = () => {
  modalTitle.value = t('ajustes.taxes.calcular_tax');
  isModalOpen.value = true;
  modalType.value = 'modalCalculateTaxes';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedTaxes.value = null;
  calculateTax.value = null;
};

const handleActive = async () => {
  const taxes = await getTaxesFromAPI(1, false, { search: 'WEB' });
  updateSecurityMeasures(taxes);
  isViewActive.value = true;
  isWeb.value = true;
};

const handleInactive = async () => {
  const taxesDeleted = await getTaxesFromAPI(1, true);
  updateSecurityMeasures(taxesDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: ITaxes) => {
  modalTitle.value = isViewActive.value
    ? `${t('ajustes.taxes.borrar_tax_con_el_id')} ${item.id}`
    : `${t('ajustes.taxes.restaurar_tax_con_el_id')} ${item.id}`;
  modalType.value = 'modalDialog';
  selectedTaxes.value = item;
  isModalOpen.value = true;
};

const condicionalRenderTable = async (isWebArg: boolean) => {
  isWeb.value = isWebArg;
  if (isWebArg) {
    filteredTableActive.value = fieldsTableActive.value.filter(
      (e: any) => e.key !== 'typePolicy',
    );
    await getTaxesFromAPI(1, false, { search: 'WEB' });
  } else {
    filteredTableActive.value = fieldsTableActive.value.filter(
      (e: any) =>
        e.key === 'id' ||
        e.key === 'indemnificationLimitMax' ||
        e.key === 'indemnificationLimitMin' ||
        e.key === 'tax' ||
        e.key === 'typePolicy' ||
        e.key === 'typePolicyTransport' ||
        e.key === 'primaMin' ||
        e.key === 'company' ||
        e.key === 'additionalPrima' ||
        e.key === 'taxInfo' ||
        e.key === 'calcTax' ||
        e.key === 'delete',
    );
    await getTaxesFromAPI(1, false, { search: 'API' });
  }
};

// Filter Functions
const handleOnSubmit = async ({ items }: any) => {
  getTaxesFromAPI(1, false, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getTaxesFromAPI(1, false, { search: isWeb.value ? 'WEB' : 'API' });
};

//CRUD
const handleCreateTax = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const data = await TaxesRepository.createTax(items);
    store.setProp({
      key: 'taxes',
      value: [...taxes.value, data],
    });
    taxes.value = [...taxes.value, data];
    $toast?.success(t('ajustes.taxes.tax_agregado_con_exito'));
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

const handleSelectTaxes = (taxesData: ITaxes) => {
  modalTitle.value = t('ajustes.taxes.actualizar_tax');
  (isModalOpen.value = true), (modalType.value = 'modalUpdateTaxes');
  selectedTaxes.value = taxesData;
};

const handleCalculateTax = async (taxesData: ITaxes) => {
  try {
    const data = await TaxesRepository.calculatePrice(taxesData);
    modalTitle.value = t('ajustes.taxes.calculo_de_tasa');
    isModalOpen.value = true;
    modalType.value = 'modalCalculateTax';
    selectedTaxes.value = { ...taxesData, calculation: data.calculation };
  } catch (error: any) {
    $toast?.error(error.message);
  }
};

const handleUpdateTaxes = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedTaxes.value) return;
  const taxForUpdate = {
    ...items,
    id: selectedTaxes.value.id,
  };

  try {
    const data = await TaxesRepository.updateTax(taxForUpdate);
    let index = store.taxes.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: 'taxes', index, value: data });
    }
    $toast?.success(t('ajustes.taxes.tax_actualizada_con_exito'));
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

const handleDeleteRestoreTaxes = async () => {
  if (selectedTaxes?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await TaxesRepository.deleteTax(selectedTaxes.value.id);
      } else {
        response = await TaxesRepository.restoreTax(selectedTaxes.value.id);
      }

      if (response === 200) {
        store.deleteItem({
          key: 'taxes',
          value: selectedTaxes?.value?.id,
        });
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error(t('global.ocurrio_un_error'));
      }
      $toast?.error(t('global.ocurrio_un_error'));
    }

    handleClose();
  }
};

const handleCustomCalculate = async ({ items, isFormValid }: any) => {
  try {
    if (!isFormValid) return;
    const data = await TaxesRepository.calculatePriceToDynamicTax(items);
    calculateTax.value = data;
  } catch (error: any) {
    calculateTax.value = error.message;
  }
};
</script>

<template>
  <Layout :title="t('ajustes.ajustes.taxes')" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-end mb-2" v-if="isViewActive">
        <button
          :class="[
            'px-5 text-white border-0 rounded-4',
            !isWeb ? 'background-blue' : 'background-blue-darken ',
          ]"
          style="margin-right: 10px"
          @click="condicionalRenderTable(true)"
        >
          WEB
        </button>
        <button
          :class="[
            'px-5 text-white border-0 rounded-4',
            isWeb ? 'background-blue' : 'background-blue-darken ',
          ]"
          @click="condicionalRenderTable(false)"
        >
          API
        </button>
      </div>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>{{ t('global.activos') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>{{ t('global.inactivos') }}</template>
          </CButton>
        </div>
        <div>
          <CButton
            class="background-pink button__form mx-2"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleOpenModalCalculate"
          >
            <template #content>{{ t('ajustes.taxes.calcular_tax') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleOpenModalCreate"
          >
            <template #content>{{ t('ajustes.taxes.agregar_tax') }}</template>
          </CButton>
        </div>
      </div>

      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content>{{ t('global.buscar') }}</template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content>{{ t('global.limpiar_filtro') }}</template>
            </CButton>
          </template>
        </Form>
      </div>

      <DataTable
        :loading="isLoadingTaxes"
        :fields="isViewActive ? filteredTableActive : fieldsTableInactive"
        :items="TaxesPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(taxInfo)="taxData">
          <div
            @click="handleSelectTaxes(taxData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(calcTax)="taxData">
          <div
            @click="handleCalculateTax(taxData.data.item)"
            class="button-icon"
          >
            <img src="/icons/calculator.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
        :size="modalType === 'modalCalculateTax' ? 'md' : 'xl'"
      >
        <template v-if="modalType === 'modalCreateTaxes'">
          <Form
            ref="refTableForm"
            :inputs="isWeb ? fieldsInputTaxesWeb : fieldsInputTaxesApi"
            @onSubmit="handleCreateTax"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('ajustes.taxes.agregar_tax') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalCalculateTaxes'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputCalculateTax"
            @onSubmit="handleCustomCalculate"
          >
            <template #buttons>
              <div v-if="calculateTax" class="mx-2">
                <p class="text-danger" v-if="!calculateTax.calculation">
                  {{ calculateTax }}
                </p>
                <div v-else class="my-4">
                  <p class="text-success">
                    Total: {{ calculateTax.calculation }}
                  </p>
                  <p class="text-success">
                    {{ t('ajustes.taxes.id_de_la_tax_aplicada') }} {{ calculateTax.tax }}
                  </p>
                </div>
              </div>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('ajustes.taxes.calcular_tax') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateTaxes'">
          <Form
            ref="refTableForm"
            :inputs="isWeb ? fieldsInputTaxesWeb : fieldsInputTaxesApi"
            @onSubmit="handleUpdateTaxes"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('ajustes.taxes.actualizar_tax') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalCalculateTax'">
          <div class="d-flex flex-column col">
            <span class="fw-bold"
              >{{ t('ajustes.taxes.tax') }}:
              <span class="fw-normal">{{ selectedTaxes?.tax }}</span></span
            >
            <span class="fw-bold"
              >{{ t('ajustes.taxes.indemnizacion_maxima') }}:
              <span class="fw-normal">{{
                selectedTaxes?.indemnificationLimitMax
              }}</span></span
            >
            <span class="fw-bold"
              >{{ t('ajustes.taxes.prima_minima') }}:
              <span class="fw-normal">{{ selectedTaxes?.primaMin }}</span></span
            >
            <span class="fw-bold"
              >{{ t('ajustes.taxes.prima_adicional') }}:
              <span class="fw-normal">{{
                selectedTaxes?.additionalPrima
              }}</span></span
            >
            <span class="fw-bold"
              >{{ t('ajustes.taxes.indemnizacion_minima') }}:
              <span class="fw-normal">{{
                selectedTaxes?.indemnificationLimitMin
              }}</span></span
            >
            <span class="fw-bold"
              >{{ t('ajustes.taxes.calculo_total') }}:
              <span class="fw-normal">{{
                selectedTaxes?.calculation
              }}</span></span
            >
          </div>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `${t('ajustes.taxes.esta_seguro_que_desea_borrar')} ${selectedTaxes?.id}?`
                : `${t('ajustes.taxes.esta_seguro_que_desea_restaurar')} ${selectedTaxes?.id}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreTaxes"
            >
              <template #content>
                <div>
                  <span>{{ t('global.confirmar') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
